// ============================================================
// ByOurConsent.com -- Individual Article Page
// ============================================================

import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import useWindowWidth from '../hooks/useWindowWidth';
import { Helmet } from 'react-helmet-async';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import api    from '../utils/api';
import toast  from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

// ============================================================
// Reaction button
// ============================================================
const ReactionBtn = ({ type, label, icon, count, reacted, onClick }) => (
    <button onClick={onClick} style={{
        display:       'flex',
        alignItems:    'center',
        gap:           '5px',
        background:    reacted ? '#ede8dc' : 'transparent',
        border:        `0.5px solid ${reacted ? '#8b1a1a' : '#c8beaa'}`,
        borderRadius:  '20px',
        padding:       '5px 12px',
        fontSize:      '12px',
        fontFamily:    'sans-serif',
        color:         reacted ? '#8b1a1a' : '#4a5060',
        cursor:        'pointer',
        transition:    'all 0.15s'
    }}>
        <span>{icon}</span>
        <span>{label}</span>
        {count > 0 && (
            <span style={{ fontWeight: 600 }}>{count}</span>
        )}
    </button>
);

// ============================================================
// ArticlePage
// ============================================================
const ArticlePage = () => {
    const { slug }     = useParams();
    const navigate     = useNavigate();
    const location     = useLocation();
    const width        = useWindowWidth();
    const isMobile     = width < 768;
    const { isAdmin }  = useAuth();
    const [article,    setArticle]    = useState(null);
    const [loading,    setLoading]    = useState(true);
    const [reactions,  setReactions]  = useState({
        upvote: false, helpful: false,
        important: false, sharing: false
    });
    const [counts, setCounts] = useState({ upvote: 0 });

    useEffect(() => {
        setLoading(true);
        api.get(`/articles/${slug}`)
            .then(res => {
                setArticle(res.data.article);
                setCounts({ upvote: res.data.article.upvote_count || 0 });
            })
            .catch(err => {
                if (err.status === 404) navigate('/articles', { replace: true });
                if (err.status === 401) toast.error('Sign in to read this article');
            })
            .finally(() => setLoading(false));
    }, [slug, navigate]);

    const handleReact = async (type) => {
        try {
            const sessionId = localStorage.getItem('boc_session') ||
                (() => {
                    const id = Math.random().toString(36).slice(2);
                    localStorage.setItem('boc_session', id);
                    return id;
                })();

            const res = await api.post(
                `/articles/${article.id}/react`,
                { reaction_type: type },
                { headers: { 'x-session-id': sessionId } }
            );

            setReactions(prev => ({ ...prev, [type]: res.data.reacted }));

            if (type === 'upvote') {
                setCounts(prev => ({
                    ...prev,
                    upvote: res.data.reacted
                        ? prev.upvote + 1
                        : Math.max(0, prev.upvote - 1)
                }));
            }
        } catch {
            toast.error('Could not save reaction');
        }
    };

    if (loading) {
        return (
            <>
                <Nav />
                <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f0e8', fontFamily: 'sans-serif', color: '#8a8070', fontSize: '14px' }}>
                    Loading article...
                </div>
                <Footer />
            </>
        );
    }

    if (!article) return null;

    return (
        <>
            <Helmet>
                <title>{article.title} — By Our Consent</title>
                <meta name="description" content={article.excerpt || article.title} />
                {article.cover_image_url && (
                    <meta property="og:image" content={article.cover_image_url} />
                )}
            </Helmet>

            <Nav />

            {/* Admin bar -- only visible to admins */}
            {isAdmin && (
                <div style={{
                    background:   '#1e2a3a',
                    borderBottom: '2px solid #8b1a1a',
                    padding:      '8px 1.5rem',
                    display:      'flex',
                    alignItems:   'center',
                    gap:          '1rem',
                    flexWrap:     'wrap'
                }}>
                    <button onClick={() => window.close() || navigate(-1)}
                        style={{ background:'transparent', border:'none', fontSize:'12px', fontFamily:'sans-serif', color:'#b8ad98', cursor:'pointer', letterSpacing:'0.06em', textTransform:'uppercase', padding:0 }}>
                        ← Back
                    </button>
                    <span style={{ color:'#2e3d50', fontSize:'12px' }}>|</span>
                    <span style={{ fontSize:'12px', fontFamily:'sans-serif', color:'#6a7a8a' }}>
                        Admin preview · {article.status?.replace('_',' ')}
                    </span>
                </div>
            )}

            {/* Breadcrumb */}
            <div style={{ background: '#ede8dc', borderBottom: '0.5px solid #c8beaa', padding: '8px 1.5rem', fontSize: '12px', fontFamily: 'sans-serif', color: '#8a8070', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <Link to="/articles" style={{ color: '#8a8070', textDecoration: 'none' }}>Articles</Link>
                <span style={{ margin: '0 6px', opacity: 0.5 }}>›</span>
                <span style={{ color: '#4a5060' }}>{article.title}</span>
            </div>

            {/* Main layout */}
            <div style={{ maxWidth: '960px', margin: '0 auto', padding: isMobile ? '1rem' : '2rem 1.5rem', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 260px', gap: '2.5rem', alignItems: 'start' }}>

                {/* Article */}
                <article>
                    {/* Tags */}
                    {article.tags?.length > 0 && (
                        <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
                            {article.tags.map(tag => (
                                <Link key={tag} to={`/articles?tag=${tag}`} style={{
                                    fontSize: '10px', fontFamily: 'sans-serif',
                                    letterSpacing: '0.1em', textTransform: 'uppercase',
                                    background: '#ede8dc', border: '0.5px solid #c8beaa',
                                    borderRadius: '20px', padding: '2px 9px',
                                    color: '#4a5060', textDecoration: 'none'
                                }}>
                                    {tag}
                                </Link>
                            ))}
                        </div>
                    )}

                    {/* Image left + title right layout */}
                    <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start', marginBottom: '1rem' }}>
                        {(article.featured_image_url || article.cover_image_url) && (
                            <img
                                src={article.cover_image_url || `http://localhost:5002${article.featured_image_url}`}
                                alt={article.title}
                                style={{ width: '130px', height: '88px', objectFit: 'cover', borderRadius: '3px', flexShrink: 0 }}
                            />
                        )}
                        <div style={{ flex: 1 }}>
                            {/* Category */}
                            {article.category_name && (
                                <div style={{ fontSize: '10px', fontFamily: 'sans-serif', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8b6a3a', marginBottom: '6px' }}>
                                    {article.category_name}
                                </div>
                            )}
                            {/* Title */}
                            <h1 style={{
                                fontSize: '30px', fontWeight: 600, color: '#1e2a3a',
                                fontFamily: "'EB Garamond', Georgia, serif",
                                lineHeight: 1.15, margin: 0
                            }}>
                                {article.title}
                            </h1>
                        </div>
                    </div>

                    {/* Meta */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.5rem', paddingBottom: '1.25rem', borderBottom: '0.5px solid #c8beaa' }}>
                        <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#1e2a3a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontFamily: 'sans-serif', color: '#e8dfc8', fontWeight: 600 }}>
                            {(article.author_name || 'ED')[0]}
                        </div>
                        <span style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#4a5060', fontWeight: 600 }}>
                            {article.author_name || 'Editorial Desk'}
                        </span>
                        <span style={{ color: '#c8beaa', fontSize: '10px' }}>·</span>
                        <span style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#8a8070' }}>
                            {article.display_date}
                        </span>
                        {article.reading_time_minutes && (
                            <>
                                <span style={{ color: '#c8beaa', fontSize: '10px' }}>·</span>
                                <span style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#8a8070' }}>
                                    {article.reading_time_minutes} min read
                                </span>
                            </>
                        )}
                        {article.unique_view_count > 0 && (
                            <>
                                <span style={{ color: '#c8beaa', fontSize: '10px' }}>·</span>
                                <span style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#8a8070' }}>
                                    {article.unique_view_count.toLocaleString()} {article.unique_view_count === 1 ? 'reader' : 'readers'}
                                </span>
                            </>
                        )}
                    </div>

                    {/* Video embed */}
                    {article.video_url && (
                        <div style={{ marginBottom: '1.5rem', borderRadius: '4px', overflow: 'hidden', background: '#000', aspectRatio: '16/9' }}>
                            <iframe
                                src={article.video_url.replace('watch?v=', 'embed/')}
                                title={article.title}
                                style={{ width: '100%', height: '100%', border: 'none' }}
                                allowFullScreen
                            />
                        </div>
                    )}

                    {/* Author detail -- inline above article body */}
                    {article.author_name && (
                        article.author_name === 'Editorial Desk' ? (
                            <div style={{ padding: '10px 1rem', background: '#ede8dc', border: '0.5px solid #c8beaa', borderRadius: '4px', marginBottom: '1.5rem', fontSize: '12px', fontFamily: 'sans-serif', color: '#6a7080' }}>
                                Published by the <strong style={{ color: '#1e2a3a' }}>Editorial Desk</strong>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '1.25rem', background: 'linear-gradient(135deg, #1e2a3a 0%, #2e3d52 100%)', border: '0.5px solid #3a4e64', borderLeft: '3px solid #8b1a1a', borderRadius: '4px', marginBottom: '1.5rem' }}>
                            {article.author_photo_url ? (
                                <img src={article.author_photo_url.startsWith('http') ? article.author_photo_url : `http://localhost:5002${article.author_photo_url}`}
                                    alt={article.author_name}
                                    style={{ width: '52px', height: '52px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '2px solid #8b1a1a' }} />
                            ) : (
                                <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: '#8b1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f5f0e8', fontSize: '20px', fontFamily: "'EB Garamond', Georgia, serif", fontWeight: 600, flexShrink: 0 }}>
                                    {article.author_name?.[0]}
                                </div>
                            )}
                            <div>
                                <div style={{ fontSize: '9px', fontFamily: 'sans-serif', letterSpacing: '0.16em', textTransform: 'uppercase', color: '#8b9a7a', marginBottom: '4px' }}>
                                    About the author
                                </div>
                                <Link to={`/author/${encodeURIComponent(article.author_name)}`}
                                    style={{ fontSize: '15px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", lineHeight: 1.2, textDecoration: 'none', display: 'block' }}>
                                    {article.author_name}
                                </Link>
                                {article.author_organization && (
                                    <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8b9a7a', marginTop: '2px' }}>{article.author_organization}</div>
                                )}
                                {article.author_bio && (
                                    <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#9aabb8', lineHeight: 1.6, marginTop: '6px' }}>
                                        {article.author_bio}
                                    </div>
                                )}
                                {article.author_website && (
                                    <a href={article.author_website} target="_blank" rel="noopener noreferrer"
                                        style={{ display: 'inline-block', background: '#8b1a1a', color: '#f5f0e8', padding: '4px 12px', fontSize: '10px', fontFamily: 'sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', textDecoration: 'none', borderRadius: '2px', marginTop: '8px' }}>
                                        Visit website →
                                    </a>
                                )}
                            </div>
                        </div>
                        )
                    )}

                    {/* Article body */}
                    <div
                        className="article-body"
                        dangerouslySetInnerHTML={{ __html: article.body_html }}
                    />

                    {/* Reactions */}
                    <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '0.5px solid #c8beaa' }}>
                        <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8a8070', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px' }}>
                            Was this useful?
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <ReactionBtn type="upvote"    label="Helpful"    icon="👍" count={counts.upvote}    reacted={reactions.upvote}    onClick={() => handleReact('upvote')} />
                            <ReactionBtn type="important" label="Important"  icon="⚡" count={0}                reacted={reactions.important} onClick={() => handleReact('important')} />
                            <ReactionBtn type="sharing"   label="Sharing this" icon="↗" count={0}             reacted={reactions.sharing}   onClick={() => handleReact('sharing')} />
                        </div>
                    </div>

                    {/* Share */}
                    <div style={{ marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '0.5px solid #c8beaa', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Share</span>
                        {[
                            { label: 'Facebook',    url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}` },
                            { label: 'X / Twitter', url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(article.title)}` },
                            { label: 'Copy link',   url: null }
                        ].map(({ label, url }) => (
                            <button key={label}
                                onClick={() => {
                                    // Track share count
                                    api.post(`/articles/${article.id}/share`).catch(() => {});
                                    if (url) window.open(url, '_blank');
                                    else {
                                        navigator.clipboard.writeText(window.location.href);
                                        toast.success('Link copied!');
                                    }
                                }}
                                style={{ background: '#ede8dc', border: '0.5px solid #c8beaa', padding: '5px 12px', fontSize: '11px', fontFamily: 'sans-serif', color: '#4a5060', cursor: 'pointer', borderRadius: '2px' }}>
                                {label}
                            </button>
                        ))}
                        {article.share_count > 0 && (
                            <span style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', marginLeft: '4px' }}>
                                {article.share_count} {article.share_count === 1 ? 'share' : 'shares'}
                            </span>
                        )}
                    </div>

                    {/* Back link */}
                    <div style={{ marginTop: '2rem' }}>
                        <Link to="/articles" onClick={() => window.scrollTo(0,0)} style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8b6a3a', textDecoration: 'none', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                            ← All articles
                        </Link>
                    </div>
                </article>

                {/* Sidebar -- desktop only shows; on mobile stacks below */}
                <aside style={{ position: isMobile ? 'static' : 'sticky', top: '72px' }}>

                    {/* Tags on this article */}
                    {article.tags?.length > 0 && (
                        <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1rem', marginBottom: '1.1rem' }}>
                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#1e2a3a', marginBottom: '8px', paddingBottom: '6px', borderBottom: '0.5px solid #c8beaa' }}>
                                Topics
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {article.tags.map(tag => (
                                    <Link key={tag} to={`/articles?tag=${tag}`} style={{ fontSize: '11px', fontFamily: 'sans-serif', background: '#ede8dc', border: '0.5px solid #c8beaa', borderRadius: '20px', padding: '3px 10px', color: '#4a5060', textDecoration: 'none', textTransform: 'capitalize' }}>
                                        {tag}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* More articles link */}
                    <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1rem', textAlign: 'center' }}>
                        <Link to="/articles" onClick={() => window.scrollTo(0,0)} style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8b1a1a', textDecoration: 'none', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                            ← Back to all articles
                        </Link>
                    </div>

                </aside>
            </div>

            <Footer />
        </>
    );
};

export default ArticlePage;
